// src/utils/constants.ts

// ============ APPLICATION ============

export const APP_NAME = 'SIM-EVAL';
export const APP_FULL_NAME = 'Sistem Informasi Manajemen Evaluasi';
export const APP_INSTITUTION = 'Universitas Buana Perjuangan Karawang';
export const APP_VERSION = '2.0.0';
export const APP_YEAR = '2026';

// ============ COLORS ============

export const COLORS = {
  primary: '#0f33ab',
  primaryLight: '#32B8C6',
  primaryDark: '#134252',
  success: '#22C55E',
  warning: '#F59E0B',
  danger: '#EF4444',
  info: '#3B82F6',
  neutral: {
    bg: '#F8F9FA',
    border: '#E5E7EB',
    textPrimary: '#1F2937',
    textSecondary: '#6B7280',
  },
} as const;

export const CHART_COLORS = {
  risk: ['#22C55E', '#F59E0B', '#F97316', '#EF4444'],
  status: ['#0f33ab', '#32B8C6', '#22C55E', '#F59E0B', '#EF4444'],
  kategori: ['#002060', '#10B981', '#F59E0B', '#8B5CF6', '#EF4444'],
  general: ['#0f33ab', '#32B8C6', '#22C55E', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#6B7280'],
} as const;

// ============ KATEGORI MASALAH ============

export const KATEGORI_MASALAH = [
  { id: 1, nama: 'Akademik', color: '#002060', icon: 'GraduationCap' },
  { id: 2, nama: 'SDM & Keuangan', color: '#10B981', icon: 'Users' },
  { id: 3, nama: 'Kemahasiswaan', color: '#F59E0B', icon: 'UserCheck' },
  { id: 4, nama: 'Riset & Inovasi', color: '#8B5CF6', icon: 'Lightbulb' },
  { id: 5, nama: 'Infrastruktur & Umum', color: '#EF4444', icon: 'Building' },
] as const;

// ============ WAREK SCOPE MAPPING ============

export const WAREK_KATEGORI_SCOPE: Record<string, number[]> = {
  wakil_rektor_1: [1],       // Akademik
  wakil_rektor_2: [2, 5],    // SDM & Keuangan, Infrastruktur
  wakil_rektor_3: [3],       // Kemahasiswaan
  wakil_rektor_4: [4],       // Riset & Inovasi
};

// ============ PAGINATION ============

export const DEFAULT_PAGE_SIZE = 10;
export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

// ============ FILE UPLOAD ============

export const MAX_FILE_SIZE_MB = 10;
export const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
export const MAX_FILES_COUNT = 10;

export const ACCEPTED_FILE_TYPES = {
  document: '.pdf,.doc,.docx',
  spreadsheet: '.xls,.xlsx,.csv',
  image: '.jpg,.jpeg,.png,.gif',
  all: '.pdf,.doc,.docx,.xls,.xlsx,.csv,.jpg,.jpeg,.png,.gif',
} as const;

export const FILE_TYPE_ICONS: Record<string, string> = {
  'application/pdf': 'FileText',
  'application/msword': 'FileText',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'FileText',
  'application/vnd.ms-excel': 'Table',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Table',
  'image/jpeg': 'Image',
  'image/png': 'Image',
  'image/gif': 'Image',
  default: 'File',
};

// ============ AUTO-SAVE ============

export const AUTO_SAVE_INTERVAL_MS = 30_000; // 30 seconds
export const DEBOUNCE_DELAY_MS = 500;

// ============ REMINDER & ESCALATION ============

export const REMINDER_DAYS = {
  first: 7,    // H-7
  second: 3,   // H-3
  final: 1,    // H-1
} as const;

export const ESCALATION_RULES = {
  overdue_days_to_escalate: 3,
  max_escalation_level: 3,
} as const;

// ============ SLA (Service Level Agreement) ============

export const SLA_DAYS: Record<string, number> = {
  rendah: 60,
  sedang: 30,
  tinggi: 14,
  kritis: 7,
};

// ============ STATUS CONFIGURATION (State Machine) ============

export interface StatusConfig {
  label: string;
  color: string;
  bgColor: string;
  textColor: string;
  description: string;
  order: number;
}

export const STATUS_CONFIG: Record<string, StatusConfig> = {
  draf: {
    label: 'Draf',
    color: '#9CA3AF', bgColor: 'bg-gray-100', textColor: 'text-gray-700',
    description: 'Laporan masih dalam penyusunan',
    order: 0,
  },
  diajukan: {
    label: 'Menunggu Persetujuan',
    color: '#3B82F6', bgColor: 'bg-blue-100', textColor: 'text-blue-700',
    description: 'Menunggu persetujuan Dekan/Ka. Lembaga',
    order: 1,
  },
  validasi_l1: {
    label: 'Disetujui Dekan',
    color: '#14B8A6', bgColor: 'bg-teal-100', textColor: 'text-teal-700',
    description: 'Disetujui Dekan, menunggu Wakil Rektor',
    order: 2,
  },
  ditolak_l1: {
    label: 'Dikembalikan (Dekan)',
    color: '#EF4444', bgColor: 'bg-red-100', textColor: 'text-red-700',
    description: 'Dikembalikan oleh Dekan untuk perbaikan',
    order: -1,
  },
  validasi_l2: {
    label: 'Disetujui Wakil Rektor',
    color: '#22C55E', bgColor: 'bg-green-100', textColor: 'text-green-700',
    description: 'Disetujui Wakil Rektor, siap diagendakan rapat',
    order: 3,
  },
  ditolak_l2: {
    label: 'Dikembalikan (Wakil Rektor)',
    color: '#DC2626', bgColor: 'bg-red-100', textColor: 'text-red-700',
    description: 'Dikembalikan oleh Wakil Rektor untuk perbaikan',
    order: -1,
  },
  antrean_rapim: {
    label: 'Antrean Rapat Pimpinan',
    color: '#6366F1', bgColor: 'bg-indigo-100', textColor: 'text-indigo-700',
    description: 'Menunggu dijadwalkan dalam Rapat Pimpinan',
    order: 4,
  },
  diputuskan: {
    label: 'Keputusan Rapat Pimpinan',
    color: '#8B5CF6', bgColor: 'bg-purple-100', textColor: 'text-purple-700',
    description: 'Keputusan tindak lanjut telah ditetapkan',
    order: 5,
  },
  berlangsung: {
    label: 'Sedang Dikerjakan',
    color: '#F59E0B', bgColor: 'bg-amber-100', textColor: 'text-amber-700',
    description: 'Tindak lanjut sedang dilaksanakan oleh PIC',
    order: 6,
  },
  menunggu_konfirmasi: {
    label: 'Menunggu Konfirmasi Selesai',
    color: '#F59E0B', bgColor: 'bg-amber-100', textColor: 'text-amber-700',
    description: 'Menunggu konfirmasi penyelesaian dari pimpinan',
    order: 6.5,
  },
  selesai: {
    label: 'Selesai',
    color: '#059669', bgColor: 'bg-emerald-100', textColor: 'text-emerald-700',
    description: 'Semua tindak lanjut telah selesai dan dikonfirmasi',
    order: 7,
  },
  diarsipkan: {
    label: 'Diarsipkan',
    color: '#64748B', bgColor: 'bg-slate-100', textColor: 'text-slate-700',
    description: 'Laporan telah diarsipkan',
    order: 8,
  },
  diselesaikan_lokal: {
    label: 'Ditangani Langsung Pimpinan',
    color: '#059669', bgColor: 'bg-emerald-100', textColor: 'text-emerald-700',
    description: 'Diselesaikan langsung tanpa Rapat Pimpinan',
    order: 7,
  },
};

// Get all status values in workflow order
export const STATUS_ORDER = Object.entries(STATUS_CONFIG)
  .filter(([_, cfg]) => cfg.order >= 0)
  .sort(([_, a], [__, b]) => a.order - b.order)
  .map(([key]) => key);

export const getStatusLabel = (status: string): string =>
  STATUS_CONFIG[status]?.label || status;

export const getStatusColor = (status: string): string =>
  STATUS_CONFIG[status]?.color || '#9CA3AF';


// ============ VALIDATION RULES ============

export const VALIDATION = {
  judul: { minLength: 10, maxLength: 500 },
  deskripsi: { minLength: 50, maxLength: 5000 },
  dampak: { maxLength: 3000 },
  akar_masalah: { maxLength: 3000 },
  comment: { minLength: 1, maxLength: 2000 },
  notulen: { maxLength: 10000 },
  ppepp_field: { minLength: 10, maxLength: 3000 },
  password: { minLength: 8, maxLength: 128 },
  name: { minLength: 2, maxLength: 255 },
  email: { maxLength: 255 },
  phone: { maxLength: 20 },
} as const;

// ============ DATE FORMATS ============

export const DATE_FORMATS = {
  display: 'dd MMMM yyyy',
  displayShort: 'dd MMM yyyy',
  displayWithTime: 'dd MMMM yyyy, HH:mm',
  displayShortWithTime: 'dd MMM yyyy, HH:mm',
  input: 'yyyy-MM-dd',
  inputDateTime: "yyyy-MM-dd'T'HH:mm",
  api: 'yyyy-MM-dd',
  apiDateTime: "yyyy-MM-dd'T'HH:mm:ss",
  time: 'HH:mm',
  monthYear: 'MMMM yyyy',
  dayName: 'EEEE',
} as const;

// ============ QUALITY SCORE ============

export const QUALITY_SCORE_WEIGHTS = {
  has_deskripsi: 20,
  has_dampak: 15,
  has_akar_masalah: 15,
  has_standar_spmi: 10,
  has_indikator_spmi: 10,
  has_attachments: 15,
  deskripsi_length_bonus: 10, // >200 chars
  has_target_selesai: 5,
} as const;

export const calculateQualityScore = (laporan: {
  deskripsi?: string;
  dampak?: string;
  akar_masalah?: string;
  standar_spmi_id?: number | null;
  indikator_spmi_id?: number | null;
  attachments?: any[];
  target_selesai?: string;
}): number => {
  let score = 0;

  if (laporan.deskripsi && laporan.deskripsi.trim().length > 0) {
    score += QUALITY_SCORE_WEIGHTS.has_deskripsi;
  }
  if (laporan.dampak && laporan.dampak.trim().length > 0) {
    score += QUALITY_SCORE_WEIGHTS.has_dampak;
  }
  if (laporan.akar_masalah && laporan.akar_masalah.trim().length > 0) {
    score += QUALITY_SCORE_WEIGHTS.has_akar_masalah;
  }
  if (laporan.standar_spmi_id) {
    score += QUALITY_SCORE_WEIGHTS.has_standar_spmi;
  }
  if (laporan.indikator_spmi_id) {
    score += QUALITY_SCORE_WEIGHTS.has_indikator_spmi;
  }
  if (laporan.attachments && laporan.attachments.length > 0) {
    score += QUALITY_SCORE_WEIGHTS.has_attachments;
  }
  if (laporan.deskripsi && laporan.deskripsi.length > 200) {
    score += QUALITY_SCORE_WEIGHTS.deskripsi_length_bonus;
  }
  if (laporan.target_selesai) {
    score += QUALITY_SCORE_WEIGHTS.has_target_selesai;
  }

  return Math.min(score, 100);
};

// ============ API ENDPOINTS ============

export const API_ENDPOINTS = {
  // Auth
  LOGIN: '/login',
  LOGOUT: '/logout',
  REGISTER: '/register',
  FORGOT_PASSWORD: '/forgot-password',
  RESET_PASSWORD: '/reset-password',
  USER: '/user',
  CHANGE_PASSWORD: '/change-password',
  UPDATE_PROFILE: '/profile',

  // Dashboard
  DASHBOARD: '/dashboard',

  // Laporan
  LAPORAN: '/laporan',
  LAPORAN_SUBMIT: (id: number) => `/laporan/${id}/submit`,
  LAPORAN_COMMENT: (id: number) => `/laporan/${id}/comment`,
  LAPORAN_ATTACHMENT: (id: number) => `/laporan/${id}/attachment`,
  LAPORAN_ATTACHMENT_DELETE: (laporanId: number, attachmentId: number) =>
    `/laporan/${laporanId}/attachment/${attachmentId}`,

  // Validasi
  VALIDASI_LEVEL1: '/validasi/level1',
  VALIDASI_LEVEL2: '/validasi/level2',
  VALIDASI_PROCESS: (id: number) => `/validasi/${id}/process`,
  VALIDASI_BATCH: '/validasi/batch',

  // RAPIM
  RAPIM: '/rapim',
  RAPIM_PESERTA: (id: number) => `/rapim/${id}/peserta`,
  RAPIM_LAPORAN: (id: number) => `/rapim/${id}/laporan`,
  RAPIM_NOTULEN: (id: number) => `/rapim/${id}/notulen`,
  RAPIM_START: (id: number) => `/rapim/${id}/start`,
  RAPIM_END: (id: number) => `/rapim/${id}/end`,

  // PPEPP
  PPEPP: '/ppepp',
  PPEPP_PROGRESS: (id: number) => `/ppepp/${id}/progress`,

  // Monitoring
  MONITORING: '/monitoring',
  MONITORING_OVERDUE: '/monitoring/overdue',
  MONITORING_PROGRESS: '/monitoring/progress',

  // Reports
  REPORTS: '/reports',
  REPORTS_GENERATE: '/reports/generate',
  REPORTS_EXPORT: '/reports/export',

  // Admin
  ADMIN_USERS: '/admin/users',
  ADMIN_UNITS: '/admin/units',
  ADMIN_ROLES: '/admin/roles',
  ADMIN_KATEGORI: '/admin/kategori',
  ADMIN_STANDAR_SPMI: '/admin/standar-spmi',
  ADMIN_INDIKATOR_SPMI: '/admin/indikator-spmi',
  ADMIN_PERIODE: '/admin/periode',
  ADMIN_SETTINGS: '/admin/settings',
  ADMIN_LOGS: '/admin/logs',

  // Notifications
  NOTIFICATIONS: '/notifications',
  NOTIFICATIONS_READ: (id: string) => `/notifications/${id}/read`,
  NOTIFICATIONS_READ_ALL: '/notifications/read-all',
  NOTIFICATIONS_SETTINGS: '/notifications/settings',
} as const;

// ============ ROUTE PATHS ============

export const ROUTES = {
  LOGIN: '/login',
  FORGOT_PASSWORD: '/forgot-password',
  RESET_PASSWORD: '/reset-password',
  DASHBOARD: '/dashboard',

  // Laporan
  LAPORAN_LIST: '/laporan',
  LAPORAN_CREATE: '/laporan/buat',
  LAPORAN_DETAIL: (id: number | string = ':id') => `/laporan/${id}`,
  LAPORAN_EDIT: (id: number | string = ':id') => `/laporan/${id}/edit`,

  // Validasi
  VALIDASI_LEVEL1: '/validasi/level1',
  VALIDASI_LEVEL2: '/validasi/level2',

  // RAPIM
  RAPIM_LIST: '/rapim',
  RAPIM_CREATE: '/rapim/buat',
  RAPIM_DETAIL: (id: number | string = ':id') => `/rapim/${id}`,
  RAPIM_PRESENTASI: (id: number | string = ':id') => `/rapim/${id}/presentasi`,

  // Monitoring
  MONITORING: '/monitoring',
  TINDAK_LANJUT_DETAIL: (id: number | string = ':id') => `/tindak-lanjut/${id}`,

  // Reports
  REPORT_BUILDER: '/reports/builder',
  REPORT_RESULT: '/reports/result',

  // Admin
  ADMIN_USERS: '/admin/users',
  ADMIN_UNITS: '/admin/units',
  ADMIN_MASTER: '/admin/master',
  ADMIN_STANDAR: '/admin/standar',
  ADMIN_SETTINGS: '/admin/settings',
  ADMIN_LOGS: '/admin/logs',

  // Profile
  PROFILE: '/profile',
  CHANGE_PASSWORD: '/change-password',
  NOTIFICATIONS: '/notifications',
} as const;

// ============ LOCAL STORAGE KEYS ============

export const STORAGE_KEYS = {
  TOKEN: 'sim_eval_token',
  USER: 'sim_eval_user',
  SIDEBAR_EXPANDED: 'sim_eval_sidebar_expanded',
  THEME: 'sim_eval_theme',
  LANGUAGE: 'sim_eval_language',
  DRAFT_PREFIX: 'sim_eval_draft_',
  LAST_ROUTE: 'sim_eval_last_route',
  TABLE_PAGE_SIZE: 'sim_eval_table_page_size',
} as const;

// ============ ERROR MESSAGES ============

export const ERROR_MESSAGES = {
  NETWORK_ERROR: 'Gagal terhubung ke server. Periksa koneksi internet Anda.',
  UNAUTHORIZED: 'Sesi Anda telah berakhir. Silakan login kembali.',
  FORBIDDEN: 'Anda tidak memiliki akses untuk melakukan tindakan ini.',
  NOT_FOUND: 'Data yang diminta tidak ditemukan.',
  VALIDATION_ERROR: 'Terdapat kesalahan pada data yang diinput.',
  SERVER_ERROR: 'Terjadi kesalahan pada server. Silakan coba lagi nanti.',
  FILE_TOO_LARGE: `Ukuran file melebihi batas maksimal ${MAX_FILE_SIZE_MB}MB.`,
  FILE_TYPE_NOT_ALLOWED: 'Tipe file tidak didukung.',
  MAX_FILES_EXCEEDED: `Maksimal ${MAX_FILES_COUNT} file per laporan.`,
  REQUIRED_FIELD: (field: string) => `${field} wajib diisi.`,
  MIN_LENGTH: (field: string, min: number) => `${field} minimal ${min} karakter.`,
  MAX_LENGTH: (field: string, max: number) => `${field} maksimal ${max} karakter.`,
} as const;

// ============ SUCCESS MESSAGES ============

export const SUCCESS_MESSAGES = {
  LOGIN: (name: string) => `Selamat datang, ${name}!`,
  LOGOUT: 'Anda berhasil keluar.',
  LAPORAN_CREATED: 'Laporan berhasil dibuat.',
  LAPORAN_UPDATED: 'Laporan berhasil diperbarui.',
  LAPORAN_SUBMITTED: 'Laporan berhasil disubmit.',
  LAPORAN_DELETED: 'Laporan berhasil dihapus.',
  DRAFT_SAVED: 'Draft berhasil disimpan.',
  VALIDASI_APPROVED: 'Laporan berhasil disetujui.',
  VALIDASI_REJECTED: 'Laporan berhasil ditolak.',
  COMMENT_ADDED: 'Komentar berhasil ditambahkan.',
  FILE_UPLOADED: 'File berhasil diupload.',
  FILE_DELETED: 'File berhasil dihapus.',
  RAPAT_CREATED: 'Rapat berhasil dibuat.',
  RAPAT_UPDATED: 'Rapat berhasil diperbarui.',
  RAPAT_STARTED: 'Rapat dimulai.',
  RAPAT_ENDED: 'Rapat selesai.',
  KEPUTUSAN_SAVED: 'Keputusan PPEPP berhasil disimpan.',
  PROGRESS_UPDATED: 'Progress berhasil diperbarui.',
  USER_CREATED: 'User berhasil ditambahkan.',
  USER_UPDATED: 'User berhasil diperbarui.',
  USER_DELETED: 'User berhasil dihapus.',
  PASSWORD_CHANGED: 'Password berhasil diubah.',
  PROFILE_UPDATED: 'Profil berhasil diperbarui.',
  SETTINGS_SAVED: 'Pengaturan berhasil disimpan.',
} as const;